import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, AlertTriangle } from 'lucide-react';

export const EXTRA_PACKAGES = {
  BIQ: 'quick_boost',
  DEEP_THINKING: 'deep_starter',
};

const BuyExtraPackageModal = ({ open, onClose, type, packages, onPurchase }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setPurchaseResult(null);
      setLoading(false);
    }
  }, [open]);

  if (!open) return null;

  const pkg = packages?.[type];
  const title = t('usage.buyExtraModal.title');
  const displayName = pkg?.display_name || type;
  const description = pkg?.description || '';
  const price = t(`usage.buyExtraModal.packages.${type}.price`);

  const handleBuy = async () => {
    setLoading(true);
    try {
      if (onPurchase) {
        const result = await onPurchase(type);
        setPurchaseResult(result);
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setPurchaseResult({
        status: false,
        message: err.message || t('usage.buyExtraModal.purchaseFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  if (purchaseResult) {
    const succeeded = purchaseResult.status !== false;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-[#4a4a4a]/70"
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          role="dialog"
          aria-modal="true"
          className="relative z-10 w-full max-w-[400px] px-[54px] rounded-[12px] bg-white dark:bg-[#2A2929] border border-[#E8EBE9] dark:border-transparent shadow-xl"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-[14px] right-[14px] p-1 rounded hover:bg-[#F0F5F2] dark:hover:bg-[#3d403e] text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2] transition-colors"
            aria-label={t('usage.buyExtraModal.close')}
          >
            <X size={20} />
          </button>

          <div className="px-4 pt-10 pb-10 text-center">
            {succeeded ? (
              <Check size={40} className="text-bloom-green mx-auto mb-4" />
            ) : (
              <AlertTriangle size={40} className="text-amber-500 mx-auto mb-4" />
            )}
            <p className="font-['Inter'] font-semibold text-[18px] text-[#282A29] dark:text-[#f0f5f2] mb-3">
              {purchaseResult.message}
            </p>

            {purchaseResult.warning && (
              <div className="flex items-start gap-2 mt-4 p-3 rounded-[8px] bg-amber-50 dark:bg-[#3d3a20] text-left">
                <AlertTriangle size={18} className="text-amber-600 dark:text-[#f5a623] flex-shrink-0 mt-0.5" />
                <p className="font-['Inter'] text-[14px] text-amber-800 dark:text-[#f5a623]">
                  {purchaseResult.warning}
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={onClose}
              className="w-full mt-8 py-2 rounded-[8px] font-['Inter'] font-medium text-[16px] bg-bloom-green text-white hover:bg-[#13b854] transition-colors"
            >
              {t('usage.buyExtraModal.close')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[#4a4a4a]/70"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="buy-extra-modal-title"
        className="relative z-10 w-full max-w-[400px] px-[54px] rounded-[12px] bg-white dark:bg-[#2A2929] border border-[#E8EBE9] dark:border-transparent shadow-xl"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-[14px] right-[14px] p-1 rounded hover:bg-[#F0F5F2] dark:hover:bg-[#3d403e] text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2] transition-colors"
          aria-label={t('usage.buyExtraModal.close')}
        >
          <X size={20} />
        </button>

        <h2 id="buy-extra-modal-title" className="font-['Inter'] font-semibold text-[20px] text-center pt-10 leading-tight text-[#282A29] dark:text-[#f0f5f2]">
          {title}
        </h2>

        <div className="px-4 pt-10 pb-10">
          <div className="text-center">
            <p className="font-['Inter'] font-bold text-[18px] text-[#282A29] dark:text-[#f0f5f2]">{displayName}</p>
            <p className="font-['Inter'] font-bold text-[18px] text-[#282A29] dark:text-[#f0f5f2] mt-3">{price}</p>
          </div>

          <div className="mt-6 pt-3 pb-3 border-t border-b border-[#E8EBE9] dark:border-[#5D625F]">
            <p className="font-['Inter'] font-normal text-[#5d625f] dark:text-[#e0e5e2] mb-4">
              {t('usage.buyExtraModal.whatsIncluded')}
            </p>
            <div className="flex items-center gap-2">
              <Check size={18} className="text-bloom-green flex-shrink-0" />
              <span className="font-['Inter'] text-[#282A29] dark:text-[#f0f5f2] text-left">{description}</span>
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-3 items-stretch">
            <button
              type="button"
              onClick={handleBuy}
              disabled={loading}
              className="w-full py-2 rounded-[8px] font-['Inter'] font-medium text-[16px] bg-bloom-green text-white hover:bg-[#13b854] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? t('usage.buyExtraModal.buying') : t('usage.buyExtraModal.buyPackage')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 rounded-[8px] font-['Inter'] font-medium text-[16px] border border-bloom-green text-bloom-green bg-transparent hover:bg-bloom-green/10 transition-colors"
            >
              {t('usage.buyExtraModal.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyExtraPackageModal;
